/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IDictionary, IImageData, IImageSettings, Image } from '@msdyn365-commerce/core';
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { ArrayExtensions, generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    Drawer,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    ICollapseProps,
    IDrawerState,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { CategoryModal } from '../../../components/categorymodal.component';
import { EnagageModal } from '../../../components/engagemodal.component';
import { CurriculaModal } from '../../../components/curriculamodal.component';
import { getUrlSync, getAsset } from '@msdyn365-commerce/core';
import { getToggleLang } from '../../../utilities/language-change';
import { IKwrHomePageLanguageAttributes } from '../../../modules/moe-kwr-homepage/moe-kwr-homepage.view';
import { MAORI_LANG, ENGLISH_LANG } from '../../../utilities/constants';
import { IMoeKwrNavigationMenuData } from './navigation-menu.data';

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    categoryImage?: IImageData[] | null;
    categoryImageAltText: string;
    drawerKeyValue: IDictionary<boolean>;
    mobileViewLabelText?: string;
    categoryMemuIsActive: boolean;
    engageMenuIsActive: boolean;
    curriculaMenuIsActive: boolean;
    mobileCurriculaIsActive: boolean;
    mobileEngageIsActive: boolean;
    mobileCategoryMenuIsActive: boolean;
    isMenuOpen: boolean;
}

/**
 *
 * NavigationMenuView component.
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState, IMoeKwrNavigationMenuData> {
    private static isBackTrack: boolean = false;

    private readonly menuItemRef: React.RefObject<HTMLElement>;

    private currentLevel: number = 0;

    private readonly _positionInSetOffset: number = 1;

    private readonly escapeKey: number = 27;

    private readonly menuNode: React.RefObject<HTMLUListElement>;

    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    constructor(props: INavigationMenuViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.menuItemRef = React.createRef();

        this.state = {
            activeMenu: undefined,
            parentMenu: undefined,
            categoryImageAltText: '',
            drawerKeyValue: {},
            categoryMemuIsActive: false,
            engageMenuIsActive: false,
            curriculaMenuIsActive: false,
            mobileCurriculaIsActive: false,
            mobileEngageIsActive: false,
            mobileCategoryMenuIsActive: false,
            isMenuOpen: false
        };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this._escFunction = this._escFunction.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            document.body.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
            document.body.addEventListener('mousedown', this._handleClickOutside);
            document.body.addEventListener('focusout', this._handleFocusOutside);
        }
    }

    public componentDidUpdate(): void {
        if (this.menuItemRef.current?.children[0] && this.props.isMobileView) {
            (this.menuItemRef.current.children[0] as HTMLElement).focus();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            document.removeEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
            document.body.removeEventListener('mousedown', this._handleClickOutside, false);
            document.body.removeEventListener('focusout', this._handleFocusOutside, false);
        }
    }

    public render(): JSX.Element | null {
        const { isMobileView, Navigation, menuItemData, resources, context } = this.props;
        const navArray = menuItemData;
        const atuaIndex = menuItemData?.findIndex(item => item.linkText === 'KAUWHATA ATUA');
        let subMenu: any;
        if (atuaIndex > -1) {
            subMenu = menuItemData[atuaIndex].subMenu ? menuItemData[atuaIndex].subMenu?.slice(1) : null;
        } else {
            subMenu = menuItemData[0].subMenu ? menuItemData[0].subMenu?.slice(1) : null;
        }
        const { data }: any = this.props;

        // Identifying Language
        const toggleLang = getToggleLang(context);
        let userLang = context.request.user.isAuthenticated && data.customerInformation.result && data.customerInformation.result.Language;
        userLang = userLang && userLang === 'mi-NZ' ? MAORI_LANG : ENGLISH_LANG;
        const userPrefLang = userLang || toggleLang;
        const preferredLanguage: any = userPrefLang === ENGLISH_LANG && toggleLang === ENGLISH_LANG ? ENGLISH_LANG : MAORI_LANG;

        // Changing language based on preferred language
        const appendLangWord = preferredLanguage === MAORI_LANG ? 'InMaori' : '';
        const KwrHomePageLanguageAttributes: IKwrHomePageLanguageAttributes = {
            languageText: resources[`languageText${appendLangWord}`],
            atuaTabName: resources[`atuaTabName${appendLangWord}`],
            engageTabName: resources[`engageTabName${appendLangWord}`],
            curriculaTabName: resources[`curriculaTabName${appendLangWord}`],
            searchTabName: resources[`searchTabName${appendLangWord}`],
            closeText: resources[`closeText${appendLangWord}`],
            introSearchText1: resources[`introSearchText1${appendLangWord}`],
            introSearchText2: resources[`introSearchText2${appendLangWord}`],
            trySearchText: resources[`trySearchText${appendLangWord}`],
            introAtuaSearchText: resources[`introAtuaSearchText${appendLangWord}`],
            introAtuaBlurbTextRanguini: resources[`introAtuaBlurbTextRanguini${appendLangWord}`],
            introAtuaBlurbTextPapatuanuku: resources[`introAtuaBlurbTextPapatuanuku${appendLangWord}`],
            introAtuaBlurbTextTangaroa: resources[`introAtuaBlurbTextTangaroa${appendLangWord}`],
            introAtuaBlurbTextTane: resources[`introAtuaBlurbTextTane${appendLangWord}`],
            introAtuaBlurbTextTumatauenga: resources[`introAtuaBlurbTextTumatauenga${appendLangWord}`],
            introAtuaBlurbTextRongo: resources[`introAtuaBlurbTextRongo${appendLangWord}`],
            introAtuaBlurbTextHaumia: resources[`introAtuaBlurbTextHaumia${appendLangWord}`],
            introAtuaBlurbTextTawhirimatea: resources[`introAtuaBlurbTextTawhirimatea${appendLangWord}`],
            introAtuaBlurbTextHineahuone: resources[`introAtuaBlurbTextHineahuone${appendLangWord}`],
            introAtuaBlurbTextWhiro: resources[`introAtuaBlurbTextWhiro${appendLangWord}`],
            introAtuaBlurbTextRuaumoko: resources[`introAtuaBlurbTextRuaumoko${appendLangWord}`],
            backButtonText: resources[`backButtonText${appendLangWord}`],
            curatedCollections: resources[`curatedCollections${appendLangWord}`]
        };

        this.currentLevel = 1;
        const mainJSURL = `${getAsset('scripts/main.js', this.props.context.request)}`;
        const codeStr = `
        <div>
          <script type="application/javascript">
          var main = document.getElementById("main");
          if(main){
             main.classList.add("main");
          }
          var body = document.getElementsByTagName("body")[0];
          if(body){
            body.id='body';
            body.classList.add("body--global-nav");
          }
          </script> 
          <script type="text/javascript"src=${mainJSURL} defer='defer'></script>                      
        </div>`;
        const engageSubMenu =
            (this.props.config.cmsNavItems && this.props.config.cmsNavItems.length > 0 && this.props.config.cmsNavItems[0]) || undefined;
        const curriculaSubMenu =
            (this.props.config.cmsNavItems && this.props.config.cmsNavItems.length > 1 && this.props.config.cmsNavItems[1]) || undefined;
        const searchURL = getUrlSync('search', this.props.context.actionContext) || '';

        return (
            <Module
                {...Navigation}
                className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent')}
            >
                {/* <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                    {isMobileView && this.state.activeMenu !== undefined && (
                        <Node {...MobileDescriptionContainer}>
                            <Node {...MobileBackButton} onClick={this._handleGoBack()} />
                            <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                        </Node>
                    )}
                </Node> */}
                {isMobileView
                    ? this._renderMobileMenus(searchURL, curriculaSubMenu, subMenu, engageSubMenu, KwrHomePageLanguageAttributes)
                    : this._renderNavigation(navArray, searchURL, KwrHomePageLanguageAttributes)}
                <ul className={`mega-dropdowns ${this.state.isMenuOpen ? 'show' : 'hidden'}`} tabIndex={0}>
                    <CategoryModal
                        index={0}
                        menuName={'Kauwhata'}
                        subMenuItems={subMenu}
                        renderButton={() => {
                            <button tabIndex={1} id='atuaToggle' className='header-menu__mega-nav-toggler'>
                                <span>{navArray[0].linkText}</span>
                            </button>;
                        }}
                        buttonClassname={'header-menu__mega-nav-toggler'}
                        context={this.props.context}
                        id={'1'}
                        typeName={'2'}
                        data={[]}
                        isModalOpen={this.state.categoryMemuIsActive}
                        btnCloseClick={() => this.handleSubmenuModal()}
                        KwrHomePageLanguageAttributes={KwrHomePageLanguageAttributes}
                        preferredLanguage={preferredLanguage}
                    ></CategoryModal>
                    {engageSubMenu && (
                        <EnagageModal
                            index={0}
                            menuName={'Engage'}
                            subMenuItems={engageSubMenu}
                            renderButton={() => {
                                <button tabIndex={1} id='atuaToggle' className='header-menu__mega-nav-toggler'>
                                    <span>{navArray[0].linkText}</span>
                                </button>;
                            }}
                            buttonClassname={'header-menu__mega-nav-toggler'}
                            context={this.props.context}
                            id={'1'}
                            typeName={'2'}
                            data={[]}
                            isModalOpen={this.state.engageMenuIsActive}
                            btnCloseClick={() => this.setEngageMenuIsActive()}
                            KwrHomePageLanguageAttributes={KwrHomePageLanguageAttributes}
                            preferredLanguage={preferredLanguage}
                        ></EnagageModal>
                    )}
                    {curriculaSubMenu && (
                        <CurriculaModal
                            index={0}
                            menuName={'Engage'}
                            subMenuItems={curriculaSubMenu}
                            renderButton={() => {
                                <button tabIndex={1} id='atuaToggle' className='header-menu__mega-nav-toggler'>
                                    <span>{navArray[0].linkText}</span>
                                </button>;
                            }}
                            context={this.props.context}
                            id={'1'}
                            typeName={'2'}
                            data={[]}
                            isModalOpen={this.state.curriculaMenuIsActive}
                            btnCloseClick={() => this.setCurriculaMenuIsActive()}
                            KwrHomePageLanguageAttributes={KwrHomePageLanguageAttributes}
                            preferredLanguage={preferredLanguage}
                        ></CurriculaModal>
                    )}
                </ul>
                <div dangerouslySetInnerHTML={{ __html: codeStr }} />
            </Module>
        );
    }

    private handleSubmenuModal() {
        if (this.state.engageMenuIsActive || this.state.curriculaMenuIsActive) {
            this.setState({
                categoryMemuIsActive: !this.state.categoryMemuIsActive,
                isMenuOpen: true,
                engageMenuIsActive: false,
                curriculaMenuIsActive: false
            });
        } else {
            this.setState({
                categoryMemuIsActive: !this.state.categoryMemuIsActive,
                isMenuOpen: !this.state.isMenuOpen,
                engageMenuIsActive: false,
                curriculaMenuIsActive: false
            });
        }
    }

    private setCurriculaMenuIsActive() {
        if (this.state.engageMenuIsActive || this.state.categoryMemuIsActive) {
            this.setState({
                curriculaMenuIsActive: !this.state.curriculaMenuIsActive,
                isMenuOpen: true,
                engageMenuIsActive: false,
                categoryMemuIsActive: false
            });
        } else {
            this.setState({
                curriculaMenuIsActive: !this.state.curriculaMenuIsActive,
                isMenuOpen: !this.state.isMenuOpen,
                engageMenuIsActive: false,
                categoryMemuIsActive: false
            });
        }
    }

    private setEngageMenuIsActive() {
        if (this.state.categoryMemuIsActive || this.state.curriculaMenuIsActive) {
            this.setState({
                engageMenuIsActive: !this.state.engageMenuIsActive,
                isMenuOpen: true,
                categoryMemuIsActive: false,
                curriculaMenuIsActive: false
            });
        } else {
            this.setState({
                engageMenuIsActive: !this.state.engageMenuIsActive,
                isMenuOpen: !this.state.isMenuOpen,
                curriculaMenuIsActive: false,
                categoryMemuIsActive: false
            });
        }
    }

    /**
     * Method to render mobile menu.
     * @returns Jsx element.
     */
    // private _renderMobileMenu(): JSX.Element {
    //     const { isMobileView, menuItemData, Navigation } = this.props;
    //     return (
    //         <Module {...Navigation} className={classnames(Navigation.className, isMobileView ? 'mobile-view' : 'desktop-view')}>
    //             {menuItemData.map((menuItem: IMenuItemData, posinset: number) => {
    //                 return this._renderDrawerMenu(menuItem, menuItemData.length, posinset + this._positionInSetOffset);
    //             })}
    //         </Module>
    //     );
    // }

    /**
     * Method to render desktop menu.
     * @returns Jsx element.
     */
    // private _renderDesktopMenu(): JSX.Element {
    //     const { MenuList, Navigation } = this.props;
    //     return (
    //         <Module {...Navigation} className={classnames(Navigation.className)}>
    //             <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
    //                 {this._renderDisplay()}
    //             </Node>
    //         </Module>
    //     );
    // }

    /**
     * Method to render drawer menu.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    // private _renderDrawerMenu(menuItem: IMenuItemData, setSize: number, posinset: number): JSX.Element | null {
    //     if (menuItem && menuItem.subMenu && ArrayExtensions.hasElements(menuItem.subMenu)) {
    //         return this._renderDrawer(menuItem, setSize, posinset);
    //     }
    //     return this._renderLinkMenuItem(menuItem, undefined, true, false, setSize, posinset);
    // }

    /**
     * Method to render drawer component.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderDrawer(menuItem: IMenuItemData, setSize?: number, posinset?: number): JSX.Element | null {
        const toggleButtonText = menuItem.linkText;
        const keyValue = this.state.drawerKeyValue;
        const buttonText = toggleButtonText !== undefined ? toggleButtonText : '';
        const keys = keyValue !== undefined ? keyValue : {};

        let isDrawerOpen = false;
        if (keys[buttonText]) {
            isDrawerOpen = true;
        }
        const colProps: ICollapseProps = { isOpen: isDrawerOpen };
        return (
            <Drawer
                role='listbox'
                toggleButtonProps={{
                    'aria-setsize': setSize,
                    'aria-posinset': posinset,
                    role: 'option'
                }}
                key={menuItem.id}
                className='ms-nav__drawer'
                openGlyph='ms-nav__drawer-open'
                onToggle={this._onDrawerChange}
                collapseProps={colProps}
                closeGlyph='ms-nav__drawer-close'
                glyphPlacement='end'
                toggleButtonText={this._renderLinkText(toggleButtonText, setSize, posinset)}
            >
                <div role='listbox'>
                    {menuItem.subMenu!.map((menuSubItem: IMenuItemData, currentPos: number) => {
                        if (ArrayExtensions.hasElements(menuSubItem.subMenu)) {
                            return this._renderDrawer(menuSubItem, menuItem.subMenu?.length, currentPos + this._positionInSetOffset);
                        }
                        return this._renderDrawerLink(menuSubItem, menuItem.subMenu?.length, currentPos + this._positionInSetOffset);
                    })}
                </div>
            </Drawer>
        );
    }

    /**
     * Method to render link.
     * @param linkText -Text on Menu link.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderLinkText(linkText: string | undefined, setSize: number | undefined, posinset: number | undefined): JSX.Element {
        return (
            <span aria-setsize={setSize} aria-posinset={posinset}>
                {linkText}
            </span>
        );
    }

    /**
     * Method to render drawer link.
     * @param item -Single Menuitem.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderDrawerLink(item: IMenuItemData, setSize: number | undefined, posinset: number): JSX.Element | null {
        if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
            return this._renderLinkMenuItem(item, undefined, true, false, setSize, posinset);
        } else if (item && item.linkText && !item.linkURL) {
            return this._renderSpanMenuItem(item);
        }
        return null;
    }

    /**
     * Method to generate menu.
     * @returns Jsx element.
     */
    // private _renderDisplay(): JSX.Element[] {
    //     const { ListItem, menuItemData, isMobileView } = this.props;
    //     const { activeMenu } = this.state;
    //     const menuItemList: JSX.Element[] = [];

    //     if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
    //         let menuItem: IMenuItemData = {};
    //         for (const menuItemDatum of menuItemData) {
    //             if (menuItemDatum && menuItemDatum.id === activeMenu) {
    //                 menuItem = menuItemDatum;
    //                 this.setState({ parentMenu: undefined });
    //                 break;
    //             }
    //             menuItem = this._getFromSubMenu(menuItemDatum) as IMenuItemData;
    //             if (menuItem && menuItem.id === activeMenu) {
    //                 break;
    //             }
    //         }

    //         menuItem &&
    //             menuItemList.push(
    //                 <Node key={menuItem.id} {...ListItem}>
    //                     {' '}
    //                     {this._createMenuItemList(menuItem)}{' '}
    //                 </Node>
    //             );
    //     } else {
    //         menuItemData.forEach((item: IMenuItemData, index: number) => {
    //             menuItemList.push(
    //                 <Node key={index} {...ListItem}>
    //                     {this._createMenuItemList(item)}
    //                 </Node>
    //             );
    //         });
    //     }

    //     return menuItemList;
    // }

    /**
     * Method to get data for submenu.
     * @param item -Single Menuitem.
     * @returns IMenuItemData.
     */
    // private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
    //     const subMenus = item && item.subMenu;
    //     if (subMenus && subMenus.length > 0) {
    //         for (let i = 0; i <= subMenus.length - 1; i++) {
    //             if (subMenus[i].id === this.state.activeMenu) {
    //                 this.setState({ parentMenu: item?.id });
    //                 return subMenus[i];
    //             }
    //             const found = this._getFromSubMenu(subMenus[i]);
    //             if (found) {
    //                 return found;
    //             }
    //         }
    //     }
    //     return null;
    // }

    /**
     * Method to create item list.
     * @param menuItemData -Single Menuitem.
     * @returns Jsx element.
     */
    // private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
    //     if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
    //         if (this.props.isMobileView && this.state.activeMenu !== undefined) {
    //             return this._renderSubMenu(menuItemData.subMenu, menuItemData.id);
    //         }
    //         return (
    //             <>
    //                 {this._renderButtonMenuItem(menuItemData)}
    //                 {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
    //             </>
    //         );
    //     } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
    //         return this._renderLinkMenuItem(menuItemData, menuItemData.id, false, true);
    //     } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
    //         return this._renderSpanMenuItem(menuItemData, menuItemData.id, true);
    //     }

    //     return null;
    // }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean): JSX.Element | null {
        const { isMobileView, ListItem } = this.props;
        const enableMultiSupportMenu = this.props.config.enableMultilevelMenu || false;
        const multiLevelSupportedMenu = this.props.config.menuLevelSupport || 3;

        // Const isParentMenu:boolean= false;
        if (activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }

        let levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;
                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                }
                let subMenu;
                const haveSubmenu = hasOptions && enableMultiSupportMenu && this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
                if (haveSubmenu) {
                    this.currentLevel++;
                    levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                    subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true);
                }
                return (
                    <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                        <Node
                            {...ListItem}
                            key={option.id}
                            className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}
                            ref={
                                this._shouldAddFocusMenuRef(idx, this.state.mobileViewLabelText, option.linkText) ? this.menuItemRef : null
                            }
                        ></Node>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });
        return this._renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu);
    }

    /**
     * Method to render button menu item.
     * @param option -Single Menuitem.
     * @param activeMenu -Active menu number.
     * @param index -Active menu index.
     * @returns Jsx element.
     */
    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <Node
                key={index}
                {...Button}
                onClick={this._handleDropdownToggle(option, activeMenu)}
                onFocus={this._closeSubmenu}
                aria-haspopup={!(this.state.activeMenu && this.state.activeMenu === option.id)}
                aria-expanded={!!(this.state.activeMenu && this.state.activeMenu === option.id)}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render link menu item.
     * @param option -Single Menuitem.
     * @param index -Active menu index.
     * @param hoverEffect -Active menu effect.
     * @param isParent -Is parent menu.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderLinkMenuItem(
        option: IMenuItemData,
        index?: number,
        hoverEffect: boolean = true,
        isParent: boolean = false,
        setSize?: number,
        posinset?: number
    ): JSX.Element | null {
        const { Link, isMobileView } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <Node
                aria-setsize={isMobileView ? setSize : undefined}
                aria-posinset={isMobileView ? posinset : undefined}
                {...Link}
                key={index}
                onFocus={isParent && this._closeSubmenu}
                target={option.shouldOpenNewTab ? '_blank' : undefined}
                onMouseOver={hoverEffect && this._updateCategoryImage(imagesource, option)}
                href={option.linkURL}
                {...attributes}
                onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render promotional link.
     * @param linkText -Link text.
     * @param linkUrl -Link url.
     * @returns Jsx element.
     */
    private _renderPromotionalLink(linkText?: string, linkUrl?: string): JSX.Element | null {
        const { Link } = this.props;
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (linkText && linkUrl) {
            return (
                <Node {...Link} href={linkUrl} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}>
                    {linkText}
                </Node>
            );
        }
        return null;
    }

    /**
     * Method to render span menu item.
     * @param option -Single Menu Item.
     * @param index -Index.
     * @param isParent -Is parent menu.
     * @returns Jsx element.
     */
    private _renderSpanMenuItem(option: IMenuItemData, index?: number, isParent: boolean = false): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span} onFocus={isParent && this._closeSubmenu}>
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render menu.
     * @param level -Menu level.
     * @param menuOptions -Menu lists.
     * @param currentItem -Menu current.
     * @param submenu -Mneu has submenu or not.
     * @returns Jsx element.
     */
    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, submenu?: boolean): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage, isMobileView, showPromotionalContent } = this.props;
        const categoryImageDisplay =
            !isMobileView && showCategoryImage && this.state.categoryImage !== null && this.state.categoryImage !== undefined && !submenu;
        const promotionalContentDisplay =
            !isMobileView && showPromotionalContent && ArrayExtensions.hasElements(this.state.categoryImage) && !submenu;
        const DivContainerClass = this.currentLevel > 2 || categoryImageDisplay ? DivContainer!.className : 'ms-nav__deafult';
        this.currentLevel = 1;
        return (
            <Node {...DivContainer} className={DivContainerClass}>
                <Node
                    {...MenuList}
                    className={classnames(
                        MenuList.className,
                        level,
                        categoryImageDisplay && 'havecateImage',
                        categoryImageDisplay &&
                            this.props.config.menuLevelSupport &&
                            this.props.config.menuLevelSupport > 2 &&
                            'navmenu-multi-level'
                    )}
                >
                    {menuOptions}
                </Node>
                <div className='category-image-container'>
                    {categoryImageDisplay &&
                        this.state.categoryImage &&
                        this.state.categoryImage.map(item => (
                            <Node {...ImageDivContainer} className={ImageDivContainer!.className}>
                                {this.state.categoryImage && this._getCategoryImage(item)}
                                {promotionalContentDisplay && this._renderPromotionalLink(item.altText, item.additionalProperties?.linkUrl)}
                            </Node>
                        ))}
                </div>
            </Node>
        );
    }

    private readonly _updateCategoryImage = (categoryImageSrc: string, option: IMenuItemData) => () => {
        const linkText = option && option.linkText ? option.linkText : '';
        const promotionalImage: IImageData[] = [{ src: categoryImageSrc, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            option.promotionalContent.map(item => {
                if (item && item.image) {
                    const imageSrc = item.image.src;
                    const promotionalItemImageSettings = item.image.imageSettings;
                    promotionalImage.push({
                        src: imageSrc,
                        altText: item.text,
                        imageSettings: promotionalItemImageSettings,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                }
            });
        }
        this.setState({
            categoryImage: promotionalImage.length > 0 ? promotionalImage : [{ src: 'empty' }],
            categoryImageAltText: linkText
        });
    };

    /**
     * Method to handle dropdwon change.
     * @param data -Menuitem data.
     * @param parentId -Menu parent.
     * @returns Jsx element.
     */
    private readonly _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            NavigationMenuView.isBackTrack = false;
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }

        // this._resetFocus();
    };

    /**
     * Method to call when focus lost from menu.
     */
    // private readonly _resetFocus = () => {
    //     if (this.props.isMobileView) {
    //         setTimeout(() => {
    //             this.menuNode && this.menuNode.current && this.menuNode.current.focus();
    //         }, 0);
    //     }
    // };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleClickOutside = (event: MouseEvent) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target as Node)) {
            this.setState({
                activeMenu: undefined,
                mobileViewLabelText: '',
                categoryImage: null
            });
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleFocusOutside = (event: FocusEvent) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.relatedTarget as Node)) {
            this._closeSubmenu();
        }
    };

    /**
     * Checks to assign a ref for focus.
     * @param index -Sub menu list index.
     * @param mobileViewLabelText -Parent user clicked Sub menu list.
     * @param optionLinkText -Sub menu list.
     * @returns Ref.
     */
    private readonly _shouldAddFocusMenuRef = (index: number, mobileViewLabelText?: string, optionLinkText?: string) => {
        if (!NavigationMenuView.isBackTrack && index === 0) {
            return true;
        }
        if (!optionLinkText || !mobileViewLabelText) {
            return false;
        }
        return mobileViewLabelText === optionLinkText;
    };

    /**
     * Method to handle close submenu event.
     */
    private _closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, categoryImage: null, mobileViewLabelText: '' });
        }
    }

    /**
     * Method to handle escape key event.
     * @param event -HTML event.
     */
    private readonly _escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode === this.escapeKey) {
            if (!this.props.isMobileView) {
                const navDrawerList = Array.from(this.menuNode.current!.childNodes);
                for (const item of navDrawerList) {
                    const navDrawerButton = item.firstChild as HTMLButtonElement;
                    if (navDrawerButton.getAttribute('aria-expanded') === 'true') {
                        navDrawerButton.focus();
                    }
                }
            }
            this._closeSubmenu();
        }
    };

    private readonly _getCategoryImage = (categoryImage?: IImageData): React.ReactNode | null => {
        if (!categoryImage || !categoryImage.src) {
            return null;
        }

        const categoryImageUrl = generateImageUrl(`${categoryImage.src}`, this.props.context.actionContext.requestContext.apiSettings);
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                sm: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                md: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                lg: { q: 'w=300&h=250&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };
        if (categoryImageUrl !== undefined) {
            // const ImageData: IImageData = { src: categoryImageUrl };
            return (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-nav-image__item'
                    {...categoryImage}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={categoryImage.imageSettings ?? this.props.config.categoryImageSettings ?? defaultImageSettings}
                    loadFailureBehavior='hide'
                    role='tabpanel'
                    id='categoryImageTag__categoryImage'
                    altText={categoryImage.altText}
                />
            );
        }
        return null;
    };

    /**
     * Method to handle escape key event.
     * @param drawerState -HTML event.
     */
    private readonly _onDrawerChange = (drawerState: IDrawerState) => {
        const { drawerKeyValue } = this.state;
        const drawerText: string = drawerState.buttonText !== undefined ? drawerState.buttonText : '';
        const newPair = { [drawerText]: drawerState.isOpen };
        this.setState({ drawerKeyValue: { ...drawerKeyValue, ...newPair } });
    };

    // private _getSlot(slotName: string): React.ReactNode[] | null {
    //     const { slots } = this.props;
    //     return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    // }

    private _renderNavigation(
        menuItemData: IMenuItemData[],
        searchURL: string,
        KwrHomePageLanguageAttributes: IKwrHomePageLanguageAttributes
    ): JSX.Element | null {
        // const breadcrumbSlot = this._getSlot('breadcrumb');
        // const logourl = `${getAsset('images/logo-kauwhata-reo.svg', this.props.context.request)}`;
        // const { logoLink, logoImage } = this.props.config;
        // const homeURL = getUrlSync('home', this.props.context.actionContext) || '';
        // const locale = this.props.context.request.locale;
        // console.log('locale:', locale);

        return (
            <>
                <ul className='ms-nav__list kwr-normal-navigation' role='menu'>
                    {menuItemData?.map((categoryItem, index: number) => {
                        return this._renderHeaderMenuItem(categoryItem, index, KwrHomePageLanguageAttributes);
                    })}
                    <li className='ms-nav__list__item' role='presentation' id='whata-patoro__mega'>
                        <button
                            tabIndex={2}
                            id='newsEventsToggle'
                            className='ms-nav__list__item__button'
                            role='menuitem'
                            onClick={() => {
                                this.setEngageMenuIsActive();
                            }}
                        >
                            <span>{KwrHomePageLanguageAttributes.engageTabName}</span>
                        </button>
                    </li>
                    <li className='ms-nav__list__item' role='presentation' id='learning-areas__mega'>
                        <button
                            tabIndex={3}
                            id='learningAreasToggle'
                            className='ms-nav__list__item__button'
                            onClick={() => {
                                this.setCurriculaMenuIsActive();
                            }}
                        >
                            <span>{KwrHomePageLanguageAttributes.curriculaTabName}</span>
                        </button>
                    </li>
                    <li className='ms-nav__list__item' role='presentation' id='kwr-navlist-item__search'>
                        <a tabIndex={4} href={searchURL} title='Search' className='ms-nav__list__item__link'>
                            <span>
                                <span className='icon--small icon--search-light'></span>
                                {KwrHomePageLanguageAttributes.searchTabName}
                            </span>
                        </a>
                    </li>
                </ul>
            </>
        );
    }

    private _renderHeaderMenuItem(
        menuItemData: IMenuItemData,
        itemIndex: number,
        KwrHomePageLanguageAttributes: IKwrHomePageLanguageAttributes
    ): JSX.Element | null {
        if (itemIndex === 0) {
            return (
                <li className='ms-nav__list__item' id='atua__mega' role='presentation'>
                    <button
                        tabIndex={1}
                        id='atuaToggle'
                        className='ms-nav__list__item__button'
                        onClick={() => {
                            this.handleSubmenuModal();
                        }}
                    >
                        {/* <span>{menuItemData.linkText}</span> */}
                        <span>{KwrHomePageLanguageAttributes.atuaTabName}</span>
                    </button>
                </li>
            );
        } else {
            return <></>;
        }
    }

    private _handleMobileNavChange(action: string) {
        if (action === 'atua') {
            this.setState({ mobileCategoryMenuIsActive: true, mobileCurriculaIsActive: false, mobileEngageIsActive: false });
        } else if (action === 'engage') {
            this.setState({ mobileCategoryMenuIsActive: false, mobileCurriculaIsActive: false, mobileEngageIsActive: true });
        } else if (action === 'curricula') {
            this.setState({ mobileCategoryMenuIsActive: false, mobileCurriculaIsActive: true, mobileEngageIsActive: false });
        } else if (action == 'back') {
            this.setState({ mobileCategoryMenuIsActive: false, mobileCurriculaIsActive: false, mobileEngageIsActive: false });
        }

        return;
    }

    private _renderMobileMenus(
        searchURL: string,
        curriculaSubMenu: any,
        subMenu: any,
        engageSubMenu: any,
        KwrHomePageLanguageAttributes: IKwrHomePageLanguageAttributes
    ) {
        return (
            <>
                <div className='overlay open' id='overlay' tabIndex={0}>
                    <nav className='overlay-menu'>
                        <nav className='headerMenuMobile no-print'>
                            <ul className='headerMenuMobile__list'>
                                <li className='headerMenuMobile__list-item'>
                                    <a href={searchURL} title='Search' className='headerMenuMobile__list-link'>
                                        {KwrHomePageLanguageAttributes.searchTabName}
                                        <i className='icon--medium icon--search-white'></i>
                                    </a>
                                </li>
                                <li className='headerMenuMobile__list-item'>
                                    <a
                                        className='headerMenuMobile__list-link'
                                        onClick={() => this._handleMobileNavChange('atua')}
                                        href='#'
                                        data-target='mobile-child-atua'
                                    >
                                        {KwrHomePageLanguageAttributes.atuaTabName}
                                        <i className='icon--small icon--chevron-right-red'></i>
                                    </a>
                                </li>
                                <li className='headerMenuMobile__list-item'>
                                    <a
                                        className='headerMenuMobile__list-link'
                                        href='#'
                                        onClick={() => this._handleMobileNavChange('engage')}
                                        data-target='mobile-child-engagement'
                                    >
                                        {KwrHomePageLanguageAttributes.engageTabName}
                                        <i className='icon--small icon--chevron-right-red'></i>
                                    </a>
                                </li>
                                <li className='headerMenuMobile__list-item'>
                                    <a
                                        className='headerMenuMobile__list-link'
                                        href='#'
                                        onClick={() => this._handleMobileNavChange('curricula')}
                                        data-target='mobile-child-curricula'
                                    >
                                        {KwrHomePageLanguageAttributes.curriculaTabName}
                                        <i className='icon--small icon--chevron-right-red'></i>
                                    </a>
                                </li>
                            </ul>

                            <a
                                href='#'
                                className={`headerMenuMobile__back-link ${(this.state.mobileCategoryMenuIsActive ||
                                    this.state.mobileCurriculaIsActive ||
                                    this.state.mobileEngageIsActive) &&
                                    'headerMenuMobile__back-link-active'}`}
                                id='mobileMenuClose'
                                onClick={() => this._handleMobileNavChange('back')}
                            >
                                <i className='icon--small icon--chevron-left-red'></i> {KwrHomePageLanguageAttributes.backButtonText}
                            </a>
                            <ul
                                className={`headerMenuMobile__list headerMenuMobile__list--child ${this.state.mobileCurriculaIsActive &&
                                    'headerMenuMobile__list--child-active'}`}
                                id='mobile-child-curricula'
                            >
                                <div className='headerMenuMobile__child-main-title'>
                                    <i className='icon icon--whata-mobile-menu'></i> {KwrHomePageLanguageAttributes.curriculaTabName}
                                </div>
                                {curriculaSubMenu &&
                                    curriculaSubMenu.subMenus &&
                                    curriculaSubMenu.subMenus.length > 0 &&
                                    curriculaSubMenu.subMenus?.map((categoryItem: any) => {
                                        return renderMoblieCurriculaMenuItem(categoryItem);
                                    })}
                            </ul>

                            <ul
                                className={`headerMenuMobile__list headerMenuMobile__list--child ${this.state.mobileCategoryMenuIsActive &&
                                    'headerMenuMobile__list--child-active'}`}
                                id='mobile-child-atua'
                            >
                                <div className='headerMenuMobile__child-main-title headerMenuMobile__child-main-title--atua'>
                                    <i className='icon icon--whata-mobile-menu'></i> {KwrHomePageLanguageAttributes.atuaTabName}
                                </div>

                                {subMenu &&
                                    subMenu.length > 0 &&
                                    subMenu?.map((categoryItem: any) => {
                                        return renderMobileMenuItem(categoryItem, this.props);
                                    })}
                            </ul>
                            <ul
                                className={`headerMenuMobile__list headerMenuMobile__list--child ${this.state.mobileEngageIsActive &&
                                    'headerMenuMobile__list--child-active'}`}
                                id='mobile-child-engagement'
                            >
                                <div className='headerMenuMobile__child-main-title'>
                                    <i className='icon icon--whata-mobile-menu'></i> {KwrHomePageLanguageAttributes.engageTabName}
                                </div>
                                {engageSubMenu &&
                                    engageSubMenu.subMenus &&
                                    engageSubMenu.subMenus.length > 0 &&
                                    engageSubMenu.subMenus?.map((categoryItem: any) => {
                                        return renderMoblieEngageMenuItem(categoryItem);
                                    })}
                            </ul>
                        </nav>
                    </nav>
                </div>
                {/* <div className='button_container' id='toggle' role='button' aria-pressed='false'>
                    <div className='close-text'>Close</div>
                    <span className='top'></span>
                    <span className='middle'></span>
                    <span className='bottom'></span>
                </div> */}
            </>
        );

        function renderMobileMenuItem(categoryItem: any, props: INavigationMenuViewProps) {
            const categoryImageUrl = generateImageUrl(
                `${categoryItem.imageSource}`,
                props.context.actionContext.requestContext.apiSettings
            );
            return (
                <>
                    <li className='headerMenuMobile__child-item'>
                        <a
                            href={categoryItem.linkURL}
                            className='headerMenuMobile__child-link headerMenuMobile__child-link--atua'
                            title={categoryItem.linkText}
                        >
                            <span className='headerMenuMobile__atua-image-wrapper'>
                                <img
                                    src={categoryImageUrl}
                                    alt={categoryItem.linkText}
                                    //title={categoryItem.linkText}
                                    className='headerMenuMobile__atua-image'
                                ></img>{' '}
                            </span>
                            <span className='headerMenuMobile__atua-title'> {categoryItem.linkText}</span>
                        </a>
                    </li>
                </>
            );
        }

        function renderMoblieCurriculaMenuItem(menuItem: any) {
            return (
                <>
                    <div className='headerMenuMobile__child-sub-heading'>{menuItem.linkText}</div>

                    {renderMobileCurriculaSubMenuItems(menuItem)}
                </>
            );
        }

        function renderMobileCurriculaSubMenuItems(subMenuItem: any) {
            return (
                <>
                    {subMenuItem &&
                        subMenuItem.subMenus &&
                        subMenuItem.subMenus.length > 0 &&
                        subMenuItem.subMenus?.map((subMenu: any) => {
                            return (
                                <>
                                    <li className='headerMenuMobile__child-item'>
                                        <a href={subMenu?.linkUrl?.destinationUrl} className='headerMenuMobile__child-link'>
                                            {subMenu.linkText}
                                            <i className='icon--small icon--external-link-grey'></i>
                                        </a>
                                    </li>
                                </>
                            );
                        })}
                </>
            );
        }

        function renderMoblieEngageMenuItem(menuItem: any) {
            return (
                <>
                    <div className='headerMenuMobile__child-sub-heading'>{menuItem.linkText}</div>
                    {renderMobileEngageSubMenuItems(menuItem)}
                </>
            );
        }

        function renderMobileEngageSubMenuItems(subMenuItem: any) {
            return (
                <>
                    {subMenuItem &&
                        subMenuItem.subMenus &&
                        subMenuItem.subMenus.length > 0 &&
                        subMenuItem.subMenus?.map((subMenu: any, idx: number) => {
                            return (
                                <>
                                    <li className='headerMenuMobile__child-item' key={idx}>
                                        <a className='headerMenuMobile__child-link' href={subMenu?.linkUrl?.destinationUrl}>
                                            {subMenu.linkText}
                                        </a>
                                    </li>
                                </>
                            );
                        })}
                </>
            );
        }
    }
}

export default NavigationMenuView;
